import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { pagesApi } from '../services/api';
import type { PageData, PageSection } from './Dashboard/PageBuilder/types';
import { Preloader } from './Preloader';
import Error404 from './Error404';

const DynamicPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<PageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (slug) {
      loadPage(slug);
    }
  }, [slug]);

  const loadPage = async (pageSlug: string) => {
    try {
      setLoading(true);
      const res = await pagesApi.getBySlug(pageSlug);
      if (res.success) {
        const raw = res.data || {};
        const normalizedSections = Array.isArray(raw.sections)
          ? raw.sections.map((s: any) => ({
              id: String(s.id ?? crypto.randomUUID()),
              section_type: s.section_type,
              section_data: typeof s.section_data === 'string'
                ? (() => {
                    try { return JSON.parse(s.section_data); } catch { return {}; }
                  })()
                : (s.section_data || {})
            }))
          : [];
        setPage({
          id: raw.id,
          page_title: raw.page_title || raw.title || '',
          page_slug: raw.page_slug || raw.slug || '',
          is_published: Boolean(raw.is_published),
          sections: normalizedSections,
          colors_settings: raw.colors_settings
        });
      } else {
        setError(true);
      }
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Preloader onLoadingComplete={() => setLoading(false)} />;
  if (error || !page) return <Error404 />;

  return (
    <div className="min-h-screen pt-24 pb-20">
      <div className="container mx-auto px-4">
        {page.sections.map((section) => (
          <SectionRenderer key={section.id} section={section} />
        ))}
      </div>
    </div>
  );
};

const SectionRenderer: React.FC<{ section: PageSection }> = ({ section }) => {
  const { section_type, section_data } = section;
  const style: React.CSSProperties = {
    color: section_data.colors?.text_color,
    backgroundColor: section_data.colors?.bg_color,
    borderColor: section_data.colors?.border_color,
    padding: section_data.padding || '2rem 0',
    margin: section_data.margin || '0',
    backgroundImage: section_data.colors?.gradient_start && section_data.colors?.gradient_end 
      ? `linear-gradient(${section_data.colors.gradient_direction || 'to right'}, ${section_data.colors.gradient_start}, ${section_data.colors.gradient_end})`
      : undefined
  };

  switch (section_type) {
    case 'heading':
    case 'title':
    case 'feature_heading': {
      const Tag = (section_data.level || 'h2') as keyof JSX.IntrinsicElements;
      return (
        <div style={style}>
           <Tag className="font-bold mb-4" style={{ fontSize: section_data.level === 'h1' ? '3rem' : '2rem' }}>
             {section_data.text}
           </Tag>
        </div>
      );
    }

    case 'paragraph':
    case 'text_area':
      return (
        <div style={style} className="prose max-w-none">
          <p className="whitespace-pre-wrap text-lg leading-relaxed">{section_data.text}</p>
        </div>
      );

    case 'hero_image':
      return (
        <div className="relative h-[60vh] flex items-center justify-center overflow-hidden rounded-xl my-8" style={style}>
          {section_data.url && (
            <img src={section_data.url} className="absolute inset-0 w-full h-full object-cover" alt="Hero" />
          )}
          <div className="relative z-10 bg-black/50 p-8 rounded text-center">
            <h1 className="text-5xl font-bold text-white mb-4">{section_data.text}</h1>
          </div>
        </div>
      );

    case 'media':
      return (
        <div className="my-8 rounded-xl overflow-hidden" style={style}>
          {section_data.type === 'video' ? (
            <video src={section_data.url} controls className="w-full rounded-xl shadow-lg" />
          ) : (
            <img src={section_data.url} className="w-full rounded-xl shadow-lg" alt="Media" />
          )}
        </div>
      );

    case 'tags':
      return (
        <div className="flex gap-3 flex-wrap my-4" style={style}>
          {section_data.tags?.map((tag, i) => (
            <span key={i} className="px-4 py-2 bg-cyan-900/30 text-cyan-300 rounded-full border border-cyan-500/30">
              {tag}
            </span>
          ))}
        </div>
      );

    case 'contact_form':
      return (
        <div className="max-w-2xl mx-auto p-8 rounded-xl border border-white/10 bg-white/5 my-8" style={style}>
          <h3 className="text-2xl font-bold mb-6">Contact Us</h3>
          <form className="space-y-4">
            <div>
              <label className="block text-sm mb-1 opacity-70">Name</label>
              <input type="text" className="w-full bg-black/30 border border-white/10 rounded p-3 focus:border-cyan-500 outline-none transition" />
            </div>
            <div>
              <label className="block text-sm mb-1 opacity-70">Email</label>
              <input type="email" className="w-full bg-black/30 border border-white/10 rounded p-3 focus:border-cyan-500 outline-none transition" />
            </div>
            <div>
              <label className="block text-sm mb-1 opacity-70">Message</label>
              <textarea className="w-full bg-black/30 border border-white/10 rounded p-3 h-32 focus:border-cyan-500 outline-none transition"></textarea>
            </div>
            <button className="px-8 py-3 bg-cyan-600 hover:bg-cyan-500 text-white rounded font-bold transition">
              Send Message
            </button>
          </form>
        </div>
      );

    case 'button':
      return (
        <div style={style} className={`my-6 flex ${section_data.align === 'center' ? 'justify-center' : section_data.align === 'right' ? 'justify-end' : 'justify-start'}`}>
          <a
            href={section_data.button_url || '#'}
            target={section_data.button_target || '_self'}
            rel={section_data.button_target === '_blank' ? 'noopener noreferrer' : undefined}
            className="inline-flex px-8 py-3 bg-cyan-600 hover:bg-cyan-500 text-white rounded font-bold transition"
          >
            {section_data.button_text || 'Button'}
          </a>
        </div>
      );

    case 'divider':
      return (
        <div className="my-6" style={style}>
          <hr
            style={{
              border: 0,
              borderTop: `${section_data.divider_thickness || 1}px ${section_data.divider_style || 'solid'} ${section_data.colors?.border_color || 'rgba(255,255,255,0.25)'}`
            }}
          />
        </div>
      );

    case 'spacer':
      return <div style={{ height: `${section_data.spacer_height || 48}px` }} />;

    case 'list':
      return (
        <ul className="list-disc list-inside space-y-2 my-4" style={style}>
          {(section_data.list_items || []).map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );

    case 'quote':
      return (
        <blockquote className="my-6 p-5 border-l-4 border-cyan-500 bg-white/5 rounded-r-xl" style={style}>
          <p className="text-xl italic">{section_data.text}</p>
          {section_data.quote_author && <footer className="mt-3 text-sm opacity-80">- {section_data.quote_author}</footer>}
        </blockquote>
      );

    case 'two_columns':
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8" style={style}>
          {(section_data.columns || [{}, {}]).slice(0, 2).map((col, i) => (
            <div key={i} className="rounded-xl border border-white/10 p-5 bg-white/5">
              {col.image_url && <img src={col.image_url} alt={col.heading || `Column ${i + 1}`} className="w-full h-48 object-cover rounded mb-3" />}
              {col.heading && <h3 className="text-2xl font-bold mb-2">{col.heading}</h3>}
              {col.text && <p className="text-gray-300 leading-relaxed">{col.text}</p>}
            </div>
          ))}
        </div>
      );

    default:
      return null;
  }
};

export default DynamicPage;
